// @flow
import type {BTOBox, BTOInfoItem} from "../COMPONENTS/BTOAddUpdatePage";
import {BTOBoxAndMagsItem} from "../COMPONENTS/BTOAddUpdatePage";
import type MagazineV2 from "../GUI_COMMON/SUPPORT/MagazineV2";

class LIBtoCache {
    
    static _db: any;
    static DB_NAME: string = "li-bto-cache";
    static STORE_NAME: string = "kv";
    static KEY_BTO_INFO: string = "btoInfo";
    
    constructor() {
        (async () => LIBtoCache.openDBAsync())(); //IIFE
    }
    
    static async openDBAsync() {
        if (!('indexedDB' in window)) {
            console.error("*** IndexedDB NOT SUPPORTED ***");
            alert("IndexedDB needs to be supported by the browser");
            return;
        }

        if (LIBtoCache._db) return;

        LIBtoCache._db = await new Promise((resolve, reject) => {
            const request = window.indexedDB.open(LIBtoCache.DB_NAME, 1);

            request.onupgradeneeded = () => {
                const db = request.result;
                if (!db.objectStoreNames.contains(LIBtoCache.STORE_NAME)) {
                    db.createObjectStore(LIBtoCache.STORE_NAME);
                }
            };

            request.onsuccess = () => resolve(request.result);
            request.onerror = () => {
                console.error("IndexedDB open error", request.error);
                reject(request.error);
            };
        });
        console.log("IndexedDB opened (BTO)", LIBtoCache._db);
    }
    
    static async ensureDBIsOpen() {
        if (LIBtoCache._db) {
            console.debug("BTO IndexedDB is already open");
            return;
        }

        console.log("opening BTO IndexedDB (ensureDBIsOpen)");
        await LIBtoCache.openDBAsync();
    }
    
    static async deleteCache() {
        console.debug("*** DELETE BTO IndexedDB ***");
        if ('indexedDB' in window) {
            await new Promise((resolve, reject) => {
                const req = window.indexedDB.deleteDatabase(LIBtoCache.DB_NAME);
                req.onsuccess = () => resolve(true);
                req.onerror = () => reject(req.error);
                req.onblocked = () => {
                    console.warn("Delete DB blocked; close other tabs.");
                };
            });
            LIBtoCache._db = null;
        }
    }

    static async getBTOInfo():BTOInfoItem {
        await LIBtoCache.ensureDBIsOpen();
        console.debug("getting BTOInfo (idb)...");
        const val = await LIBtoCache._idbGet(LIBtoCache.KEY_BTO_INFO);
        if (val === undefined || val === null || val === "") return {};
        return val; 
    }

    static async setBTOInfo(btoInfo:BTOInfoItem) {
        await LIBtoCache.ensureDBIsOpen();
        console.debug("saving BTOInfo (idb)...");
        await LIBtoCache._idbPut(LIBtoCache.KEY_BTO_INFO, btoInfo);
    }

    static async clearBTOInfo(initialValue={}) {
        console.debug("clearing BTO (idb)...");
        await LIBtoCache.setBTOInfo(initialValue);
    }

    static async clearAll() {
        await LIBtoCache.setBTOInfo(null);
    }

    static async getBTOBoxAndMagList():Promise<BTOBoxAndMagsItem[]> {
        const btoInfo = await LIBtoCache.getBTOInfo();
        return btoInfo?.boxesAndChildMags??[]; 
    }

    static async setBTOBoxList(boxesAndMags:BTOBoxAndMagsItem[], updateSavedFlagTo:boolean|null=null) {
        const btoInfo = await LIBtoCache.getBTOInfo();
        btoInfo.boxesAndChildMags=boxesAndMags;
        if(updateSavedFlagTo!==null) {
            btoInfo.saved=updateSavedFlagTo;
        }
        await LIBtoCache.setBTOInfo(btoInfo); // updated
    }
    
    static async getBTOOrderIdAndSaveStatus(): {storedOrderId:number,isSaved:boolean} {
        let btoInfo = await LIBtoCache.getBTOInfo();
        return {storedOrderId:btoInfo?.orderId??-1,isSaved:btoInfo?.saved??true}; 
    }

    static async addBoxesToBTOList(boxesAndMags:BTOBoxAndMagsItem[]) {
        let addedOne=false;
        const curList = await LIBtoCache.getBTOBoxAndMagList();
        
        for(let i=0;i<boxesAndMags.length;i++) { // check for duplicates
            if(!curList.find(item => item.box.id===boxesAndMags[i].box.id)) { // add box AND mags since not found
                curList.push(boxesAndMags[i]);
                addedOne=true;
            }
        }
        await LIBtoCache.setBTOBoxList(curList,addedOne?false:null); // null here means "don't update"
    }

    /**
     * remove entire boxes (and their child mags) from the BTO boxes that there were part of
     * @param boxesWithMags
     * @returns {Promise<void>}
     */
    static async removeBoxesFromBTOList(boxesWithMags: BTOBoxAndMagsItem[]) {
        console.debug("removing boxes (and child mags) from BTO list...");
        const curList = await LIBtoCache.getBTOBoxAndMagList();

        const updatedList = curList.filter((item) => 
                    !boxesWithMags.find((bmItem) => bmItem.box.id === item.box.id));

        const removedAny = curList.length !== updatedList.length;
        await LIBtoCache.setBTOBoxList(updatedList, removedAny ? false : null); // null means "don't update saved flag"
    }

    /**
     * remove individual mags from the BTO boxes that there were part of
     * @param parentBox
     * @param magsToRemove
     * @returns {Promise<void>}
     */
    static async removeMagsFromBTOList(parentBox: BTOBox, magsToRemove: MagazineV2[]) {
        console.debug(`removing ${magsToRemove.length} mags from box '${parentBox.barcode}' from BTO list...`);
        const curList = await LIBtoCache.getBTOBoxAndMagList();

        const boxToUpdate:BTOBoxAndMagsItem = curList.find((item) => item.box.id === parentBox.id);
        boxToUpdate.childMags = boxToUpdate.childMags.filter((item) => !magsToRemove.find((mag) => mag.id === item.id));

        const updatedList = curList.filter((item) => item.box.id !== boxToUpdate.box.id); // remove the box
        updatedList.push(boxToUpdate); // update the box
        const removedAny = curList.length !== updatedList.length;
        await LIBtoCache.setBTOBoxList(updatedList, removedAny ? false : null); // null means "don't update saved flag"
    }

    /**
     * unpick box and child mags from BTO (this assumes that the unpicking has already been done at the server)
     * @param boxAndChildMags
     * @param removeToo if true, remove the box and mags from the BTO
     * @returns {Promise<BTOBoxAndMagsItem[]>} the updated list
     */
    static async unpickBoxAndChildMagsFromBTO(boxAndChildMags: BTOBoxAndMagsItem, removeToo:boolean=false):Promise<BTOBoxAndMagsItem[]> {
        console.debug(`unpick box id '${boxAndChildMags.box.id}' and its mags from BTO (removing too=${removeToo})...`);
        const curList = await LIBtoCache.getBTOBoxAndMagList();

        const boxAndChildMagsToUpdate:BTOBoxAndMagsItem = 
                curList.find((item) => item.box.id === boxAndChildMags.box.id);
        if(removeToo) {
            curList.splice(curList.indexOf(boxAndChildMagsToUpdate),1);
        }
        else { // just unpick
            boxAndChildMagsToUpdate.box.datePickCompleted = null;
            boxAndChildMagsToUpdate.childMags.forEach((mag) => mag.datePickCompleted = null);
        }
        await LIBtoCache.setBTOBoxList(curList, null); // don't update the saved flag
        return curList;
    }

    /**
     * add/update a box refernce to the given box in this cache
     * @param box
     * @param refText
     * @returns {Promise<void>}
     */
    static async addReferenceToBox(box:BTOBox, refText:string) {
        console.debug(`adding reference '${refText}' to box id '${box.id}'...`);
        const curList = await LIBtoCache.getBTOBoxAndMagList();
        const boxToUpdate:BTOBoxAndMagsItem = curList.find((item) => item.box.id === box.id);
        boxToUpdate.box.foreignReferenceName = refText;
        await LIBtoCache.setBTOBoxList(curList, null); // don't update the saved flag
    }

    /**
     * set the complete text for the BTO to that which is provided.
     * this is used for updating a note visually after the server has had a note added to it
     * after a BTO is completed.
     * @param fullNoteText
     * @returns {Promise<void>}
     */
    static async updateBTONote(fullNoteText:string) {
        const btoInfo = await LIBtoCache.getBTOInfo();
        btoInfo.notes = fullNoteText;
        await LIBtoCache.setBTOInfo(btoInfo);
    }

    // --- Internal IDB helpers ---
    static _getStore(mode: any): any {
        const db = LIBtoCache._db;
        if (!db) {
            throw new Error('IndexedDB not open');
        }
        const tx = db.transaction(LIBtoCache.STORE_NAME, mode);
        return tx.objectStore(LIBtoCache.STORE_NAME);
    }

    static async _idbGet(key: string): Promise<any> {
        return new Promise((resolve, reject) => {
            try {
                const store = LIBtoCache._getStore('readonly');
                const req = store.get(key);
                req.onsuccess = () => resolve(req.result);
                req.onerror = () => reject(req.error);
            } catch (e) {
                reject(e);
            }
        });
    }

    static async _idbPut(key: string, value: any): Promise<void> {
        return new Promise((resolve, reject) => {
            try {
                const store = LIBtoCache._getStore('readwrite');
                const req = store.put(value, key);
                req.onsuccess = () => resolve();
                req.onerror = () => reject(req.error);
            } catch (e) {
                reject(e);
            }
        });
    }
}

export default LIBtoCache;